import React from 'react';
import { useTheme, Image } from 'tamagui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

import { CobuStack, CobuText, CobuView } from '@cobu/ui';
import { Strings } from '../../constants/Strings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Gathering, EventFilterType } from '@cobu/types';
import { formatDateTime } from '../../utils/dateTime';

interface EventItemProps {
  gathering: Gathering;
  currentFilter: EventFilterType;
  isLive: boolean;
  onPress: (gathering: Gathering) => void;
}

export function EventItem({ gathering, currentFilter, isLive, onPress }: EventItemProps) {
  const theme = useTheme();

  const handlePress = () => {
      onPress(gathering);
  };

  const getAttendanceLabel = () => {
    const { rsvps = 0, isAttending } = gathering;
    const s = Strings.get().events.list.item;
    const isPastGathering = currentFilter === 'past';

    if (isPastGathering) {
      return `${rsvps} ${rsvps === 1 ? s.personSingularAttended : s.peoplePluralAttended}`;
    } else {
      return (rsvps && rsvps > -1) || isAttending
        ? `${rsvps} ${rsvps === 1 ? s.personSingularAttending : s.peoplePluralAttending}`
        : s.rsvpNow;
    }
  };

  const getFormattedDateTime = () => {
    return formatDateTime(
      gathering.scheduledStartTime,
      gathering.buildingTimezone,
      'ddd, MMM DD h:mm A'
    );
  };

  const imageSource = { uri: gathering.image || gathering.groupImage };

  const renderStatusLabels = () => {
    return (
      <>
        {currentFilter === 'upcoming' && gathering.isAttending && (
          <CobuStack backgroundColor="$cobuTopaz" position="absolute" right={0} bottom={15} paddingVertical={2} paddingHorizontal="$4" borderTopLeftRadius="$3" borderBottomLeftRadius="$3">
            <CobuText color="$white" fontSize="$0" fontWeight="bold">
              • {Strings.get().events.list.item.attending}
            </CobuText>
          </CobuStack>
        )}
        {isLive && (
          <CobuStack backgroundColor="$cobuTopaz" position="absolute" left={0} top={20} paddingVertical={2} paddingHorizontal="$4" borderTopRightRadius="$3" borderBottomRightRadius="$3">
            <CobuText color="$white" fontSize="$0" fontWeight="bold">
              • {Strings.get().events.list.item.happeningNow}
            </CobuText>
          </CobuStack>
        )}
        {gathering.cancelled && (
          <CobuStack
            position="absolute"
            // t={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor="$cobuPink" 
            alignItems="center"
            paddingVertical="$3" 
          >
              <CobuText color="$white" fontSize="$1" fontWeight="bold">
                {Strings.get().events.list.item.cancelled}
              </CobuText>
          </CobuStack>
        )}
      </>
    );
  };

  const renderContentSection = () => {
    return (
      <CobuStack gap="$1">
        {/* Group Name and Date */}
        <CobuText
          color={'$cobuDarkBlue'}
          fontSize="$1"
          fow="$bold"
          marginTop="$1"
          numberOfLines={2}
          ellipsizeMode="tail"
          textTransform="uppercase"
        >
          {gathering.groupName} • {getFormattedDateTime()}
        </CobuText>

        {/* Event Title */}
        <CobuText
          color={gathering.cancelled ? ('$cobuDarkBlue50' as any) : ('$cobuDarkBlue' as any)}
          fontSize="$4"
          fontWeight="bold"
          marginTop="$1"
          numberOfLines={2}
          ellipsizeMode="tail"
          style={{
            textDecorationLine: gathering.cancelled ? 'line-through' : 'none',
          }}
        >
          {gathering.title}
        </CobuText>

        {/* Attendance Count */}
        <CobuStack layout="horizontal" alignItems="center" gap="$2">
          <CheckCircleIcon
            style={{
              color: gathering.cancelled ? "$cobuDarkBlue50" : "$cobuTeal",
              width: 16,
              height: 16,
            }}
          />
          <CobuText
            color={gathering.cancelled ? ('$cobuDarkBlue50' as any) : ('$cobuDarkBlue' as any)}
            fontSize="$2"
            fow="500"
          >
            {getAttendanceLabel()}
          </CobuText>
        </CobuStack>
      </CobuStack>
    );
  };

  const renderImageSection = () => {
    return (
      <CobuStack flex={1} gap="$0" flexShrink={0} minHeight={140}>
        <Image source={imageSource} objectFit="cover" borderRadius={'$1'} f={1} />
        {renderStatusLabels()}
      </CobuStack>
    );
  };

  return (
    <CobuStack mx="$2" my="$1" height={'$60'} gap="$0" flex={1} marginBottom="$6" bc="$backgroundSecondary">
      <CobuView onPress={handlePress} flex={1}>
        <CobuStack f={1}>
          {renderImageSection()}
          {renderContentSection()}
        </CobuStack>
      </CobuView>
    </CobuStack>
  );
}

